import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Autocomplete, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useInvoiceParentFiltersDashboard, useFetchSecondDashboardData } from './hooks';
import { sortDropdownOptions } from '../utils';

export default function DateFilter({ get_dashboard_hook }) {
	const minDate = dayjs().subtract(30, 'day').startOf('day');
	const maxDate = dayjs().startOf('day');
	const [fromValue, setFromValue] = React.useState(() =>
		dayjs().subtract(30, 'day').startOf('day')
	);
	const [ToValue, setToValue] = React.useState(() => dayjs().startOf('day'));
	const [Filters, setFilters] = useState([]);

	const Data = useSelector((p) => p.dashSlice);

	const [selectedVendor, setSelectedVendor] = useState('');
	const [selectedCategory, setSelectedCategory] = useState('');
	const [selectedClient, setSelectedClient] = useState('');

	const getfilterHook = useInvoiceParentFiltersDashboard();

	useEffect(() => {
		if (getfilterHook?.data) {
			setFilters(getfilterHook.data.data);
		}
	}, [getfilterHook?.data, getfilterHook?.isSuccess]);

	useEffect(() => {
		setDebounceSearch();
	},[])


	const dispatch = useDispatch();
	function setDebounceSearch() {
		get_dashboard_hook.mutate({
			category: selectedCategory?.category_id || null,
			client: selectedClient?.client_id || null,
			vendor: selectedVendor?.vendor_id || null,
			datefrom: fromValue ? dayjs(fromValue.$d).format('MM-DD-YYYY') : null,
			dateto: ToValue ? dayjs(ToValue.$d).add(1, 'day').format('MM-DD-YYYY') : null,
		});
		
	}



	function getUniqueClients(
		lst,
		selectedVendor = null,
		selectedCategory = null
	) {
		const uniqueClients = [];
		const seenClientIds = new Set();

		lst
			?.filter((item) => {
				// If no vendor or category selected, include all
				if (!selectedVendor && !selectedCategory) return true;
				// If both vendor and category selected, match both
				if (selectedVendor && selectedCategory) {
					return (
						item.vendor_id === selectedVendor?.vendor_id &&
						item.category_id === selectedCategory?.category_id
					);
				}
				// If only vendor selected
				if (selectedVendor) {
					return item.vendor_id === selectedVendor?.vendor_id;
				}
				// If only category selected
				if (selectedCategory) {
					return item.category_id === selectedCategory?.category_id;
				}
				return true;
			})
			?.forEach((item) => {
				if (!seenClientIds.has(item.client_id)) {
					seenClientIds.add(item.client_id);
					uniqueClients.push({
						client_id: item.client_id,
						client: item.client,
					});
				}
			});

		return sortDropdownOptions(uniqueClients, (o) => o.client);
	}

	function getUniqueVendors(
		lst,
		selectedClient = null,
		selectedCategory = null
	) {
		const uniqueVendors = [];
		const seenVendorIds = new Set();
		lst
			?.filter((item) => {
				// If no vendor or category selected, include all
				if (!selectedClient && !selectedCategory) return true;
				// If both vendor and category selected, match both
				if (selectedClient && selectedCategory) {
					return (
						item.client_id === selectedClient?.client_id &&
						item.category_id === selectedCategory?.category_id
					);
				}
				// If only vendor selected
				if (selectedCategory) {
					return item.category_id === selectedCategory?.category_id;
				}
				// If only category selected
				if (selectedClient) {
					return item.client_id === selectedClient?.client_id;
				}
				return true;
			})
			?.forEach((item) => {
				if (!seenVendorIds.has(item.vendor_id)) {
					seenVendorIds.add(item.vendor_id);
					uniqueVendors.push({
						vendor_id: item.vendor_id,
						vendor: item.vendor,
					});
				}
			});

		return sortDropdownOptions(uniqueVendors, (o) => o.vendor);
	}

	function getUniqueCategories(
		lst,
		selectedVendor = null,
		selectedClient = null
	) {
		const uniqueCategories = [];
		const seenCategoryIds = new Set();

		lst
			?.filter((item) => {
				// If no vendor or category selected, include all
				if (!selectedClient && !selectedVendor) return true;
				// If both vendor and category selected, match both
				if (selectedClient && selectedVendor) {
					return (
						item.client_id === selectedClient?.client_id &&
						item.vendor_id === selectedVendor?.vendor_id
					);
				}
				// If only vendor selected
				if (selectedVendor) {
					return item.vendor_id === selectedVendor?.vendor_id;
				}
				// If only category selected
				if (selectedClient) {
					return item.client_id === selectedClient?.client_id;
				}
				return true;
			})
			?.forEach((item) => {
				if (!seenCategoryIds.has(item.category_id)) {
					seenCategoryIds.add(item.category_id);
					uniqueCategories.push({
						category_id: item.category_id,
						category_name: item.category_name,
					});
				}
			});

		return sortDropdownOptions(uniqueCategories, (o) => o.category_name);
	}

	const uniqueClients = useMemo(
		() => getUniqueClients(Filters, selectedVendor, selectedCategory),
		[Filters, selectedClient, selectedVendor, selectedCategory]
	);
	const uniqueVendors = useMemo(
		() => getUniqueVendors(Filters, selectedClient, selectedCategory),
		[Filters, selectedClient, selectedVendor, selectedCategory]
	);
	const uniqueCategories = useMemo(
		() => getUniqueCategories(Filters, selectedVendor, selectedClient),
		[Filters, selectedClient, selectedVendor, selectedCategory]
	);

	const isFromInvalid = !fromValue;
	const isToInvalid = !ToValue;
	const areDatesInvalid = isFromInvalid || isToInvalid;

	return (
		<>
			<div className="md:flex items-center gap-3 flex-wrap xl:flex-nowrap text-sm multiDashFltr">

				<div className="grow missDD">
					<Autocomplete
						id="category"
						options={uniqueCategories}
						getOptionLabel={(option) =>
							typeof option === 'string' ? option : option.category_name
						}
						isOptionEqualToValue={(option, value) =>
							option.category_id === value.category_id
						}
						value={selectedCategory}
						onChange={(event, newValue) => {
							setSelectedCategory(newValue);
						}}
						loading={getfilterHook.isFetching}
						loadingText="Loading..."
						renderInput={(params) => (
							<TextField {...params} label="Categories" fullWidth />
						)}
						renderOption={(props, option) => (
							<li {...props} key={option['category_id']}>
								{option['category_name']}
							</li>
						)}
						slotProps={{
								popper: {
									placement: 'bottom-start', 							
								},
							}}
					/>
				</div>

				<div className="grow missDD">
					<div className="lightT">
						<Autocomplete
							id="client"
							options={uniqueClients}
							getOptionLabel={(option) =>
								typeof option === 'string' ? option : option.client
							}
							isOptionEqualToValue={(option, value) =>
								option.client_id === value.client_id
							}
							value={selectedClient}
							onChange={(event, newValue) => {
								setSelectedClient(newValue);
							}}
							loading={getfilterHook.isFetching}
							loadingText="Loading..."
							renderInput={(params) => (
								<TextField {...params} label="Clients" fullWidth />
							)}
							renderOption={(props, option) => (
								<li {...props} key={option['client_id']}>
									{option['client']}
								</li>
							)}
							slotProps={{
								popper: {
									placement: 'bottom-start', 							
								},
							}}
						/>
					</div>
				</div>

				<div className="grow missDD">
					<div className="lightT">
						<Autocomplete
							id="vendor"
							options={uniqueVendors}
							getOptionLabel={(option) =>
								typeof option === 'string' ? option : option.vendor
							}
							isOptionEqualToValue={(option, value) =>
								option.vendor_id === value.vendor_id
							}
							value={selectedVendor}
							onChange={(event, newValue) => {
								setSelectedVendor(newValue);
							}}
							loading={getfilterHook.isFetching}
							loadingText="Loading..."
							renderInput={(params) => (
								<TextField {...params} label="Vendors" fullWidth />
							)}
							renderOption={(props, option) => (
								<li {...props} key={option['vendor_id']}>
									{option['vendor']}
								</li>
							)}
							slotProps={{
								popper: {
									placement: 'bottom-start', 							
								},
							}}
						/>
					</div>
				</div>


				<div className="dateHldr flex items-center gap-3">
					<div>From</div>
					<div className="relative">
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DemoContainer components={['DatePicker']}>
								<DatePicker
									slotProps={{
										field: { clearable: true },
										textField: {
											error: isFromInvalid,
										},
									}}
									value={fromValue || null}
									minDate={minDate}
									maxDate={ToValue && ToValue.isBefore(maxDate) ? ToValue : maxDate}
									onChange={(event) => {
										setFromValue(event);
									}}
								/>
							</DemoContainer>
						</LocalizationProvider>
						{isFromInvalid && (
							<p className="absolute left-0 top-full text-red-600 text-xs mt-0.5 ml-1 whitespace-nowrap">
								Required
							</p>
						)}
					</div>
				</div>

				<div className="dateHldr flex items-center gap-3">
					<div>To</div>
					<div className="relative">
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DemoContainer components={['DatePicker']}>
								<DatePicker
									slotProps={{
										field: { clearable: true },
										textField: {
											error: isToInvalid,
										},
									}}
									value={ToValue || null}
									minDate={fromValue && fromValue.isAfter(minDate) ? fromValue : minDate}
									maxDate={maxDate}
									onChange={(newValue) => setToValue(newValue)}
								/>
							</DemoContainer>
						</LocalizationProvider>
						{isToInvalid && (
							<p className="absolute left-0 top-full text-red-600 text-xs mt-0.5 ml-1 whitespace-nowrap">
								Required
							</p>
						)}
					</div>
				</div>
				<div className="flex gap-1">
					
					<button
						type="button"
						disabled={get_dashboard_hook.isPending || areDatesInvalid}
						onClick={setDebounceSearch}
						className="bg-blue-800 hover:bg-indigo-700 w-full px-4 py-2 h-10 rounded-md text-white flex items-center font-black disabled:bg-slate-300 disabled:text-slate-700"
					>
						<span className="material-icons mr-1 text-sm">refresh</span>
						Reload
					</button>
				</div>
			</div>
		</>
	);
}
